import { createMemo, For, Show } from "solid-js";
import {
  columnHeaderForFeature,
  formatPerformanceDriversSampleCount,
  straightLineCellHeatClass,
  straightLineColumnScales,
  type PerformanceDriversTableRow,
  type PointOfSail,
} from "../../utils/performanceDriversModelFit";
import { performanceDriversStraightLineExplanation } from "../../utils/performanceDriversExplanation";

export interface PerformanceDriversChannelTableProps {
  columns: string[];
  rows: PerformanceDriversTableRow[];
  displayNames?: Record<string, string>;
  pointOfSail: PointOfSail;
  loading?: boolean;
  error?: string | null;
}

function formatChannelValue(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toFixed(1);
}

function isStraightLineRowEmpty(row: PerformanceDriversTableRow): boolean {
  return row.straightLineEmpty ?? row.skipped;
}

export default function PerformanceDriversChannelTable(props: PerformanceDriversChannelTableProps) {
  const columnScales = createMemo(() => straightLineColumnScales(props.rows, props.columns));
  const hasData = createMemo(() => props.rows.some((row) => !isStraightLineRowEmpty(row)));

  return (
    <section class="performance-drivers-table-section">
      <h3 class="performance-drivers-section-title">Straight line</h3>
      <p class="performance-drivers-section-explanation">
        {performanceDriversStraightLineExplanation(props.pointOfSail)}
      </p>

      <Show when={props.loading}>
        <p class="performance-drivers-empty">Loading straight line data…</p>
      </Show>

      <Show when={!props.loading && props.error}>
        <p class="performance-drivers-empty">{props.error}</p>
      </Show>

      <Show when={!props.loading && !props.error}>
        <Show
          when={props.columns.length > 0 && props.rows.length > 0 && hasData()}
          fallback={
            <p class="performance-drivers-empty cheat-sheet-empty-message">
              No straight line data for this configuration and point of sail. Try another selection or wind band.
            </p>
          }
        >
          <div class="data-table-container">
            <div class="scrollable-container">
              <table class="maneuvers-table performance-drivers-table performance-drivers-straight-line-table">
                <thead>
                  <tr>
                    <th class="performance-drivers-col-tws centered">TWS</th>
                    <For each={props.columns}>
                      {(col) => <th>{columnHeaderForFeature(col, props.displayNames)}</th>}
                    </For>
                    <th class="performance-drivers-col-count centered">Count</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={props.rows}>
                    {(row) => (
                      <tr class={isStraightLineRowEmpty(row) ? "performance-drivers-row-skipped" : undefined}>
                        <td class="performance-drivers-col-tws centered">{row.twsBin}</td>
                        <For each={props.columns}>
                          {(col) => {
                            const val = row.channelValues[col];
                            const empty = isStraightLineRowEmpty(row);
                            const heatClass = empty
                              ? "performance-drivers-cell-empty"
                              : straightLineCellHeatClass(val, col, columnScales());
                            return <td class={heatClass}>{formatChannelValue(val)}</td>;
                          }}
                        </For>
                        <td class="performance-drivers-col-count centered performance-drivers-cell-empty">
                          {formatPerformanceDriversSampleCount(row.sampleCount)}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          </div>
        </Show>
      </Show>
    </section>
  );
}
